import type { TaxonomyMode } from "@/api/submissions.service";

export type TaxonomyFilterParams = {
  publicationId?: number;
  editionId?: string;
  regionId?: number;
  rankingEntityId?: string;
  practiceAreaId?: number;
  bpaId?: string;
};

type TaxonomyAxis = "publication" | "region" | "practiceArea";

type TaxonomyOption = {
  id: number | string | null;
  editionId?: string | null;
  rankingEntityId?: string | null;
  bpaId?: string | null;
};

type PublicationOption = {
  name: string;
  edition?: string | null;
};

const AXES = {
  publication: { legacyParam: "publicationId", newAxisParam: "editionId" },
  region: { legacyParam: "regionId", newAxisParam: "rankingEntityId" },
  practiceArea: { legacyParam: "practiceAreaId", newAxisParam: "bpaId" },
} as const;

const NUMERIC = /^\d+$/;

export function parseTaxonomyMode(value: string | null | undefined): TaxonomyMode {
  return value === "new" ? "new" : "legacy";
}

export function taxonomyValueFitsMode(value: string, mode: TaxonomyMode): boolean {
  if (!value || value === "all") return true;
  return NUMERIC.test(value) === (mode === "legacy");
}

export function taxonomyUrlParam(axis: TaxonomyAxis, mode: TaxonomyMode): string {
  return mode === "new" ? AXES[axis].newAxisParam : AXES[axis].legacyParam;
}

export function taxonomyFilterParams(
  axis: TaxonomyAxis,
  value: string,
  mode: TaxonomyMode,
): TaxonomyFilterParams {
  if (!value || value === "all") return {};
  const { legacyParam, newAxisParam } = AXES[axis];
  if (mode === "new") return NUMERIC.test(value) ? {} : { [newAxisParam]: value };
  return NUMERIC.test(value) ? { [legacyParam]: Number(value) } : {};
}

export function taxonomyOptionFitsMode(
  axis: TaxonomyAxis,
  option: TaxonomyOption,
  mode: TaxonomyMode,
): boolean {
  return mode === "new" ? Boolean(option[AXES[axis].newAxisParam]) : option.id !== null;
}

export function taxonomyOptionValue(
  axis: TaxonomyAxis,
  option: TaxonomyOption,
  mode: TaxonomyMode,
): string {
  return mode === "new" ? (option[AXES[axis].newAxisParam] ?? "") : String(option.id);
}

export function publicationOptionLabel(option: PublicationOption): string {
  return option.edition ? `${option.name} — ${option.edition}` : option.name;
}
